import { ValidateSchemaFunction } from '@pogokid/schema'
import { Optional } from '@pogokid/util'
import {
  CreatedFields,
  DeletedFields,
  OmitCreated,
  SchemaWithId,
} from '@soniq/schema'
import {
  QueryResponse,
  SingleResponse,
  throwOnFetchError,
} from './Response'
import {
  ActionParams,
  OrderByOptions,
  ResourceRequestOptions,
  UpsertSchema,
} from './types'
import {
  FetchResponse,
  fetchWithAuth,
  ResponseJsonType,
} from './fetch'
import { normaliseIsoDateTimeString } from '@pogokid/util/datetime'
import { EntityReadStore, EntityWriteStore } from './EntityStore'
import { AppError } from './AppError'

export class HttpReadStore<Schema extends CreatedFields>
  implements EntityReadStore<Schema>
{
  protected endpoint: string
  protected columns: Array<keyof Schema> | undefined
  protected defaultOrder:
    | Partial<Record<keyof Schema, OrderByOptions>>
    | undefined

  constructor(endpoint: string) {
    this.endpoint = endpoint
  }

  protected getApiEndpoint(profileId: string): string {
    return `/v1/entity/${profileId}/${this.endpoint}`
  }

  /**
   * Map each row from query data into the Schema.
   *
   * This is useful if you need to manipulate the row
   * data before returning.
   *
   * @param data
   * @protected
   */
  protected mapQueryData<
    Returns extends SchemaWithId<Schema> = SchemaWithId<Schema>
  >(data: Optional<Returns[]>): Returns[] {
    const result = data ?? []
    return result.map((row) => ({
      ...row,
      created_at: normaliseIsoDateTimeString(row.created_at),
    }))
  }

  protected async fetchWithAuth<
    ResponseJson extends ResponseJsonType,
    Body = undefined
  >(
    options: ResourceRequestOptions & {
      method: 'get' | 'post' | 'put' | 'delete'
      endpoint: string
      searchParams?: URLSearchParams
      body?: Body
    }
  ): Promise<FetchResponse<ResponseJson>> {
    if (!options.accessToken)
      throw new Error('Access token is required')
    return fetchWithAuth<ResponseJson, Body>({
      accessToken: options.accessToken,
      baseUrl: options.apiBaseUrl,
      endpoint: options.endpoint,
      searchParams: options.searchParams,
      method: options.method,
      body: options.body,
    })
  }

  async fetchById(
    options: ResourceRequestOptions,
    profileId: string,
    id: string
  ): Promise<SingleResponse<SchemaWithId<Schema>>> {
    const result = await this.fetchWithAuth<
      SingleResponse<SchemaWithId<Schema>>
    >({
      ...options,
      method: 'get',
      endpoint: `${this.getApiEndpoint(profileId)}/${id}`,
    })

    if (result.status === 200) {
      return { data: result.data }
    } else if (result.status === 404) {
      return { data: null }
    } else {
      return {
        data: null,
        error:
          result.error || new Error('Failed to fetch by ID'),
      }
    }
  }

  async fetchByIds(
    options: ResourceRequestOptions,
    profileId: string,
    ids: string[]
  ): Promise<QueryResponse<SchemaWithId<Schema>>> {
    const result = await this.fetchWithAuth<
      QueryResponse<SchemaWithId<Schema>>
    >({
      ...options,
      method: 'get',
      endpoint: `${this.getApiEndpoint(
        profileId
      )}?by_ids=${ids.join(',')}`,
    })

    if (result.status === 200) {
      return { data: this.mapQueryData(result.data) }
    } else {
      return {
        data: [],
        error:
          result.error || new Error('Failed to fetch by IDs'),
      }
    }
  }

  async fetchQuery(
    options: ResourceRequestOptions,
    profileId: string,
    filters: Partial<Schema> = {},
    order:
      | Partial<Record<keyof Schema, OrderByOptions>>
      | undefined = this.defaultOrder
  ): Promise<QueryResponse<SchemaWithId<Schema>>> {
    if (!profileId) throw new Error('Profile id is required')
    // Build query parameters from filters
    const queryParams = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value))
      }
    })

    // Add ordering parameters
    if (order) {
      Object.entries(order).forEach(([column, orderOptions]) => {
        if (orderOptions) {
          queryParams.append('order_by', column)
          queryParams.append(
            'order_direction',
            orderOptions.ascending ? 'asc' : 'desc'
          )
        }
      })
    }

    const queryString = queryParams.toString()
    const endpoint = `${this.getApiEndpoint(profileId)}${
      queryString ? `?${queryString}` : ''
    }`

    const result = await this.fetchWithAuth<
      QueryResponse<SchemaWithId<Schema>>
    >({
      ...options,
      method: 'get',
      endpoint,
    })

    if (result.status === 200) {
      return { data: this.mapQueryData(result.data) }
    } else {
      return {
        data: [],
        error:
          result.error || new Error('Failed to fetch query'),
      }
    }
  }
}

export class HttpReadWriteStore<Schema extends CreatedFields>
  extends HttpReadStore<Schema>
  implements EntityReadStore<Schema>, EntityWriteStore<Schema>
{
  readonly validate: ValidateSchemaFunction<Schema>

  constructor(
    endpoint: string,
    validate: ValidateSchemaFunction<Schema>
  ) {
    super(endpoint)
    this.validate = validate
  }

  async create(
    options: ResourceRequestOptions,
    profileId: string,
    userId: string,
    data: OmitCreated<Schema>
  ): Promise<SingleResponse<SchemaWithId<Schema>>> {
    // Add created fields
    let createData = {
      ...data,
      created_at: new Date().toISOString(),
      created_by: userId,
    } as Schema

    // Validate if validator is provided
    if (this.validate) {
      const { isValid, cleanedValues, errors } =
        this.validate(createData)
      if (!isValid) {
        return {
          data: null,
          error: new Error(
            `Validation failed: ${JSON.stringify(
              errors,
              null,
              2
            )}`
          ),
        }
      }
      createData = cleanedValues as Schema
    }

    const result = await this.fetchWithAuth<
      SingleResponse<SchemaWithId<Schema>>,
      Schema
    >({
      ...options,
      method: 'post',
      endpoint: this.getApiEndpoint(profileId),
      body: createData,
    })

    if (result.status === 201 || result.status === 200) {
      return { data: result.data }
    } else {
      return {
        data: null,
        error: result.error || new Error('Failed to create'),
      }
    }
  }

  async update(
    options: ResourceRequestOptions,
    profileId: string,
    { id, ...data }: SchemaWithId<Partial<Schema>>
  ): Promise<SingleResponse<SchemaWithId<Schema>>> {
    // Add updated_at timestamp
    const updateData = {
      ...data,
      updated_at: new Date().toISOString(),
    }

    const result = await this.fetchWithAuth<
      SingleResponse<SchemaWithId<Schema>>,
      typeof updateData
    >({
      ...options,
      method: 'put',
      endpoint: `${this.getApiEndpoint(profileId)}/${id}`,
      body: updateData,
    })

    if (result.status === 200) {
      return { data: result.data }
    } else {
      return {
        data: null,
        error: result.error || new Error('Failed to update'),
      }
    }
  }

  async upsert(
    options: ResourceRequestOptions,
    profileId: string,
    data: UpsertSchema<Schema>[]
  ): Promise<QueryResponse<SchemaWithId<Schema>>> {
    // Validate each item if validator is provided
    if (this.validate) {
      const validatedData = data.map(({ id, ...itemData }) => {
        const { isValid, cleanedValues, errors } = this.validate(
          itemData,
          {
            allowPartial: true,
          }
        )
        if (!isValid) {
          throw new Error(
            `Validation failed: ${JSON.stringify(
              errors,
              null,
              2
            )}`
          )
        }
        return { id, ...cleanedValues } as UpsertSchema<Schema>
      })
      data = validatedData
    }

    const result = await this.fetchWithAuth<
      QueryResponse<SchemaWithId<Schema>>,
      UpsertSchema<Schema>[]
    >({
      ...options,
      method: 'put',
      endpoint: this.getApiEndpoint(profileId),
      body: data,
    })

    if (result.status === 200) {
      return { data: this.mapQueryData(result.data) }
    } else {
      return {
        data: [],
        error: result.error || new Error('Failed to upsert'),
      }
    }
  }

  async action(
    options: ResourceRequestOptions,
    profileId: string,
    params: ActionParams
  ): Promise<SingleResponse<{ ok: boolean }>> {
    const result = await throwOnFetchError(
      this.fetchWithAuth<
        SingleResponse<{ ok: boolean }>,
        { type: string } & Record<string, unknown>
      >({
        ...options,
        method: 'post',
        endpoint: `${this.getApiEndpoint(profileId)}/action`,
        body: params.data,
      })
    )

    if (result.status === 200) {
      return { data: result.data || { ok: true } }
    } else {
      throw (
        result.error ||
        new AppError('There was a problem with the action')
      )
    }
  }
}

export class HttpReadWriteDeleteStore<
    Schema extends CreatedFields & DeletedFields
  >
  extends HttpReadWriteStore<Schema>
  implements EntityReadStore<Schema>, EntityWriteStore<Schema>
{
  async delete(
    options: ResourceRequestOptions,
    profileId: string,
    ids: string[]
  ): Promise<SingleResponse<null>> {
    // For soft deletes, we'll update each record with deleted_at timestamp
    const promises = ids.map(async (id) => {
      const result = await this.fetchWithAuth<
        SingleResponse<SchemaWithId<Schema>>,
        { deleted_at: string }
      >({
        ...options,
        method: 'put',
        endpoint: `${this.getApiEndpoint(profileId)}/${id}`,
        body: { deleted_at: new Date().toISOString() },
      })
      return result
    })

    try {
      await Promise.all(promises)
      return { data: null }
    } catch (error) {
      return { data: null, error: error as Error }
    }
  }
}
